import { useState } from 'react';
import { Plus, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import apiService from '../services/api';
import { format, parseISO } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const FRMS: React.FC = () => {
  const [crewMemberId] = useState<number | undefined>();

  const { data: records, loading, error } = useApi(
    () => apiService.getFRMSRecords({ crew_member_id: crewMemberId }),
    [crewMemberId]
  );

  const getFatigueLevel = (score?: number) => {
    if (!score) return { class: 'badge-inactive', label: 'No Data', icon: Minus };
    if (score >= 8) return { class: 'badge-expired', label: 'High Risk', icon: TrendingUp };
    if (score >= 5) return { class: 'badge-pending', label: 'Moderate', icon: Minus };
    return { class: 'badge-active', label: 'Low Risk', icon: TrendingDown };
  };

  const chartData = records?.map((record) => ({
    date: format(parseISO(record.duty_date), 'MMM dd'),
    'Duty Hours': record.duty_hours,
    'Flight Hours': record.flight_hours,
    'Rest Hours': record.rest_hours,
    'Fatigue Score': record.fatigue_score || 0,
  }));

  const avgFatigueScore = (records?.reduce((acc, r) => acc + (r.fatigue_score || 0), 0) ?? 0) / (records?.length || 1);
  const avgDutyHours = (records?.reduce((acc, r) => acc + r.duty_hours, 0) ?? 0) / (records?.length || 1);
  const avgRestHours = (records?.reduce((acc, r) => acc + r.rest_hours, 0) ?? 0) / (records?.length || 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">FRMS</h1>
          <p className="mt-1 text-sm text-gray-400">
            Fatigue Risk Management System - Monitor crew fatigue and rest
          </p>
        </div>
        <button
          onClick={() => alert('Add FRMS Record form coming soon')}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Add FRMS Record
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Avg Fatigue Score</p>
          <p className="mt-1 text-2xl font-bold text-white">{avgFatigueScore.toFixed(1)}</p>
          <div className="mt-2 flex items-center gap-1">
            <TrendingDown className="h-4 w-4 text-green-400" />
            <span className="text-xs text-green-400">-12% from last week</span>
          </div>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Avg Duty Hours</p>
          <p className="mt-1 text-2xl font-bold text-white">{avgDutyHours.toFixed(1)}</p>
          <div className="mt-2 flex items-center gap-1">
            <Minus className="h-4 w-4 text-gray-400" />
            <span className="text-xs text-gray-400">No change</span>
          </div>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">Avg Rest Hours</p>
          <p className="mt-1 text-2xl font-bold text-white">{avgRestHours.toFixed(1)}</p>
          <div className="mt-2 flex items-center gap-1">
            <TrendingUp className="h-4 w-4 text-green-400" />
            <span className="text-xs text-green-400">+8% from last week</span>
          </div>
        </div>
        <div className="glass-card p-4">
          <p className="text-sm text-gray-400">High Risk Cases</p>
          <p className="mt-1 text-2xl font-bold text-red-400">
            {records?.filter((r) => (r.fatigue_score || 0) >= 8).length || 0}
          </p>
          <div className="mt-2 flex items-center gap-1">
            <TrendingDown className="h-4 w-4 text-green-400" />
            <span className="text-xs text-green-400">-3 from yesterday</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData && chartData.length > 0 && (
        <div className="glass-card p-6">
          <h2 className="mb-4 text-xl font-semibold text-white">Fatigue Trends</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                  color: '#fff',
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="Duty Hours" stroke="#0EA5E9" strokeWidth={2} />
              <Line type="monotone" dataKey="Flight Hours" stroke="#8B5CF6" strokeWidth={2} />
              <Line type="monotone" dataKey="Rest Hours" stroke="#10B981" strokeWidth={2} />
              <Line type="monotone" dataKey="Fatigue Score" stroke="#EF4444" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Records Table */}
      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="glass-card p-6 text-center text-red-400">{error.message}</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Duty Hours</th>
                <th>Flight Hours</th>
                <th>Rest Hours</th>
                <th>Fatigue Score</th>
                <th>Sleep Quality</th>
                <th>Alertness</th>
                <th>Risk Level</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {records?.map((record) => {
                const fatigueLevel = getFatigueLevel(record.fatigue_score);
                const FatigueIcon = fatigueLevel.icon;

                return (
                  <tr key={record.id}>
                    <td className="font-medium text-white">
                      {format(parseISO(record.duty_date), 'MMM dd, yyyy')}
                    </td>
                    <td>{record.duty_hours.toFixed(1)} hrs</td>
                    <td>{record.flight_hours.toFixed(1)} hrs</td>
                    <td className="text-green-400">{record.rest_hours.toFixed(1)} hrs</td>
                    <td>
                      <span
                        className={
                          (record.fatigue_score || 0) >= 8
                            ? 'text-red-400'
                            : (record.fatigue_score || 0) >= 5
                            ? 'text-yellow-400'
                            : 'text-green-400'
                        }
                      >
                        {record.fatigue_score?.toFixed(1) || 'N/A'}
                      </span>
                    </td>
                    <td>
                      {record.sleep_quality ? (
                        <span className="badge-inactive capitalize">{record.sleep_quality}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {record.alertness_level ? (
                        <span className="badge-inactive capitalize">{record.alertness_level}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`flex items-center gap-1 ${fatigueLevel.class}`}>
                        <FatigueIcon className="h-3 w-3" />
                        {fatigueLevel.label}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => alert(`FRMS Record Details:\nDate: ${format(parseISO(record.duty_date), 'MMM dd, yyyy')}\nDuty Hours: ${record.duty_hours}\nFlight Hours: ${record.flight_hours}\nRest Hours: ${record.rest_hours}\nFatigue Score: ${record.fatigue_score || 'N/A'}`)}
                        className="text-sm text-sky-400 hover:text-sky-300"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FRMS;
